import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Activity, ShieldCheck } from "lucide-react";
import { EASE, MaskedLine } from "@/components/motion";

const APP_URL = "https://app.uzakyonetim.com.tr";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden pb-24 pt-36 md:pb-32 md:pt-48"
    >
      <div className="hero-grid-bg pointer-events-none absolute inset-0" aria-hidden="true" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-6 md:px-12 lg:grid-cols-[1.2fr_1fr]">
        <motion.div style={{ y: textY }}>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35, ease: EASE }}
            className="mb-8 inline-flex items-center gap-2 rounded-full border border-[#003366]/15 bg-white px-4 py-1.5"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#336699] opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#003366]" />
            </span>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#003366]">
              Uzaktan Yönetim ve Destek Platformu
            </span>
          </motion.div>

          <h1 className="font-display text-5xl font-bold leading-[1.02] tracking-tighter text-[#0A0A0A] sm:text-6xl lg:text-7xl xl:text-8xl">
            <MaskedLine delay={0.45}>Tüm BT altyapınız,</MaskedLine>
            <MaskedLine delay={0.58}>
              <span className="text-[#003366]">tek panelden,</span>
            </MaskedLine>
            <MaskedLine delay={0.71}>her yerden.</MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
            className="mt-8 max-w-xl text-base leading-relaxed text-[#525252] md:text-lg"
          >
            Sunucularınızı, bilgisayarlarınızı ve ağ cihazlarınızı UzakYönetim RMM ile
            merkezi olarak izleyin, yönetin ve saniyeler içinde uzaktan destek verin.
            Ofise gitmeden, her şey kontrolünüz altında.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <motion.a
              href={APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-button"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex items-center gap-2 rounded-full bg-[#003366] px-8 py-4 text-base font-semibold text-white transition-colors duration-300 hover:bg-[#336699]"
            >
              Uygulamaya Giriş
              <ArrowUpRight className="h-5 w-5" strokeWidth={1.5} />
            </motion.a>
            <a
              href="#ozellikler"
              data-testid="hero-secondary-button"
              className="link-underline text-base font-semibold text-[#0A0A0A]"
            >
              Özellikleri Keşfet
            </a>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.7, ease: EASE }}
          className="relative hidden lg:block"
        >
          <motion.div style={{ y: imgY }} className="relative">
            <div
              className="pointer-events-none absolute inset-0 -m-10 rounded-full"
              style={{
                background:
                  "radial-gradient(circle at 50% 45%, rgba(51,102,153,0.14) 0%, transparent 65%)",
              }}
              aria-hidden="true"
            />
            <img
              src="/assets/logo-mark.png"
              alt="UzakYönetim RMM — bulut logosu"
              data-testid="hero-image"
              className="relative mx-auto w-full max-w-lg drop-shadow-[0_24px_48px_rgba(0,51,102,0.22)]"
              loading="eager"
            />

            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -left-10 top-12 flex items-center gap-3 rounded-2xl border border-black/5 bg-white/80 px-5 py-4 backdrop-blur-xl"
            >
              <Activity className="h-5 w-5 text-[#003366]" strokeWidth={1.5} />
              <div>
                <p className="text-sm font-bold text-[#0A0A0A]">%99,9</p>
                <p className="text-xs text-[#525252]">Çalışma süresi</p>
              </div>
            </motion.div>

            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              className="absolute -right-6 bottom-16 flex items-center gap-3 rounded-2xl border border-black/5 bg-white/80 px-5 py-4 backdrop-blur-xl"
            >
              <ShieldCheck className="h-5 w-5 text-[#003366]" strokeWidth={1.5} />
              <div>
                <p className="text-sm font-bold text-[#0A0A0A]">Uçtan Uca</p>
                <p className="text-xs text-[#525252]">Şifreli bağlantı</p>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
